import React, { FC, useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Button, Modal, Checkbox } from '@clearcover/atlas';
import classnames from 'classnames';

import { useLazyQuoteApi } from 'Shared/services/Api';
import { Column, ConfirmationModal, FormInput, Row } from 'Shared/components';

import {
  AccidentType,
  FormValuesType,
  GetDetailsResponseType,
  transformAppDetails,
  ViolationType,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  ClientOptionsType,
  GetConfigResponseType,
} from 'QuoteApp/services/endpoints/GetConfig';
import styles from './styles.module.css';
import { useEdit, useFieldData } from 'QuoteApp/utils/contexts';
import { getFieldErrors } from 'QuoteApp/utils';
import { useAppContext } from 'QuoteApp/services/providers';
import { calcualteDaysInMonth } from 'Shared/utils';

import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks';

type IncidentFormProps = {
  driverId: string;
  variant: 'accident' | 'violation';
  incidentId: string;
  config: GetConfigResponseType['drivers'];
};

export const IncidentForm: FC<IncidentFormProps> = ({
  driverId,
  variant,
  incidentId,
  config,
}) => {
  const { appId, refreshRate, details, setDetails } = useAppContext();
  const {
    setFieldValue,
    handleBlur,
    touched,
    errors,
    values,
    setStatus,
    initialValues,
  } = useFormikContext<FormValuesType>();

  const { assumeAtFaultFlag, collectDayOfAccidentFlag, collectTypeOfLossFlag } =
    useQuoteAppFeatureFlags();
  const { editingId } = useEdit();
  const { alwaysShowErrors, serverErrors } = useFieldData();

  const readOnly = editingId !== driverId;
  const inputVariant = readOnly ? 'readOnly' : 'selectAutocomplete';

  const [{ loading: removingIncident }, postRemoveIncident] =
    useLazyQuoteApi<GetDetailsResponseType>({
      method: 'POST',
      url: `/insurance_applications/${appId}/drivers/${driverId}/${variant}s/${incidentId}/delete`,
    });

  const incidents: Record<string, AccidentType | ViolationType> =
    variant === 'accident'
      ? values.drivers[driverId].accidents
      : values.drivers[driverId].violations;

  const isRemoveIncidentCommandAvailable = useMemo(() => {
    const incidentCommands =
      variant === 'accident'
        ? details.commands.drivers[driverId].accidents
        : details.commands.drivers[driverId].violations;
    return incidentCommands[incidentId]?.some(
      (c) => c.name === `remove_${variant}` && c.runnable
    );
  }, [details.commands.drivers, driverId, incidentId, variant]);

  const getAccidentInputProps = useCallback(
    (fieldName: keyof AccidentType, required = true) => {
      const fullFieldName = `drivers.${driverId}.accidents.${incidentId}.${fieldName}`;
      return {
        className: readOnly ? styles.readOnlyValue : undefined,
        name: fullFieldName,
        onChange: (value: string) => setFieldValue(fullFieldName, value),
        onBlur: handleBlur,
        disabled: readOnly,
        value: values.drivers[driverId].accidents[incidentId][fieldName],
        errorMessage: getFieldErrors<FormValuesType>(
          fullFieldName,
          required,
          alwaysShowErrors,
          errors,
          serverErrors,
          values,
          touched,
          initialValues
        ),
      };
    },
    [
      initialValues,
      serverErrors,
      alwaysShowErrors,
      driverId,
      readOnly,
      errors,
      handleBlur,
      incidentId,
      setFieldValue,
      touched,
      values,
    ]
  );

  const getViolationInputProps = useCallback(
    (fieldName: keyof ViolationType, required = true) => {
      const fullFieldName = `drivers.${driverId}.violations.${incidentId}.${fieldName}`;
      return {
        className: readOnly ? styles.readOnlyValue : undefined,
        name: fullFieldName,
        onChange: (value: string) => setFieldValue(fullFieldName, value),
        onBlur: handleBlur,
        disabled: readOnly,
        value: values.drivers[driverId].violations[incidentId][fieldName],
        errorMessage: getFieldErrors<FormValuesType>(
          fullFieldName,
          required,
          alwaysShowErrors,
          errors,
          serverErrors,
          values,
          touched,
          initialValues
        ),
      };
    },
    [
      initialValues,
      serverErrors,
      alwaysShowErrors,
      driverId,
      readOnly,
      errors,
      handleBlur,
      incidentId,
      setFieldValue,
      touched,
      values,
    ]
  );

  const daysOptions = (
    variantObject: Record<string, AccidentType | ViolationType>
  ) => {
    const obj: ClientOptionsType = {};

    const daysInMonth = calcualteDaysInMonth(
      variantObject[incidentId].month,
      variantObject[incidentId].year
    );

    for (let i = 1; i <= daysInMonth; i++) {
      obj[i] = i.toString();
    }

    return obj;
  };

  return (
    <div className={styles.wrapper}>
      {variant === 'accident' ? (
        <div
          data-testid="driver-accident-form-row"
          className={styles.accidentRow}
        >
          <Row className={styles.inputs}>
            <Column variant="sixth">
              <FormInput
                {...getAccidentInputProps('month')}
                label="Month"
                data-testid="accident-month-field"
                variant={inputVariant}
                options={config.accidents.month}
              />
            </Column>
            {collectDayOfAccidentFlag && (
              <Column variant="eighth">
                <FormInput
                  {...getAccidentInputProps('day')}
                  label="Day"
                  data-testid="accident-day-field"
                  variant={inputVariant}
                  options={daysOptions(values.drivers[driverId].accidents)}
                />
              </Column>
            )}
            <Column variant="sixth">
              <FormInput
                {...getAccidentInputProps('year')}
                label="Year"
                data-testid="accident-year-field"
                variant={inputVariant}
                options={config.accidents.year}
              />
            </Column>
            {config.accidents.collectAccidentType && (
              <Column variant="sixth">
                <FormInput
                  {...getAccidentInputProps('accidentType')}
                  label="Accident Type"
                  data-testid="accident-type-field"
                  variant={inputVariant}
                  options={config.accidents.accidentTypeOptions}
                />
              </Column>
            )}
            {config.accidents.collectLossInformation && (
              <Column variant="sixth">
                <FormInput
                  {...getAccidentInputProps('lossType')}
                  label="Loss Type"
                  data-testid="accident-loss-type-field"
                  variant={inputVariant}
                  options={config.accidents.lossOptions}
                />
              </Column>
            )}
            {collectTypeOfLossFlag && (
              <Column variant={readOnly ? 'third' : 'quarter'}>
                <FormInput
                  {...getAccidentInputProps('typeOfLoss')}
                  label="Select type of loss"
                  data-testid="type-of-loss-field"
                  variant={inputVariant}
                  options={config.accidents.typeOfLoss?.options || {}}
                />
              </Column>
            )}
            {!assumeAtFaultFlag && (
              <Column
                variant={'threeFourths'}
                className={styles.atFaultCheckboxWrapper}
              >
                <Checkbox
                  name={`drivers.${driverId}.accidents.${incidentId}.atFault`}
                  value={/true/i.test(
                    values.drivers[driverId].accidents[incidentId]['atFault']
                  )}
                  label="This was an at-fault accident"
                  data-testid="accident-at-fault-field"
                  onChange={(value: boolean) =>
                    setFieldValue(
                      `drivers.${driverId}.accidents.${incidentId}.atFault`,
                      value.toString()
                    )
                  }
                  disabled={readOnly}
                />
              </Column>
            )}
          </Row>
        </div>
      ) : (
        <div data-testid="driver-violation-form-row">
          <Row className={styles.wrapper}>
            <Column variant="eighth">
              <FormInput
                {...getViolationInputProps('month')}
                label="Month"
                data-testid="violation-month-field"
                variant={inputVariant}
                options={config.violations.month}
              />
            </Column>
            {collectDayOfAccidentFlag && (
              <Column variant="eighth">
                <FormInput
                  {...getViolationInputProps('day')}
                  label="Day"
                  data-testid="violation-day-field"
                  variant={inputVariant}
                  options={daysOptions(values.drivers[driverId].violations)}
                />
              </Column>
            )}
            <Column variant="eighth">
              <FormInput
                {...getViolationInputProps('year')}
                label="Year"
                data-testid="violation-year-field"
                variant={inputVariant}
                options={config.violations.year}
              />
            </Column>
            <Column variant="half">
              <FormInput
                {...getViolationInputProps('violationType')}
                label="Violation Type"
                data-testid="violation-type-field"
                variant={inputVariant}
                options={config.violations.violationType}
              />
            </Column>
            {config.violations.askForSr22 && (
              <Column variant="eighth">
                <FormInput
                  {...getViolationInputProps('sr22')}
                  label="SR-22"
                  data-testid="violation-sr-twenty-two-field"
                  variant={inputVariant}
                  options={{
                    true: 'Yes',
                    false: 'No',
                  }}
                />
              </Column>
            )}
          </Row>
        </div>
      )}
      <div className={classnames(styles.deleteWrapper)}>
        {!readOnly && (
          <Modal
            toggle={(openModal) => (
              <Button
                size="sm"
                variant="danger-secondary"
                className={styles.delete}
                data-testid={`delete-${variant}-button`}
                title={`Remove ${variant}`}
                isSubmitting={removingIncident}
                onClick={openModal}
                disabled={readOnly || !isRemoveIncidentCommandAvailable}
              >
                Remove
              </Button>
            )}
          >
            {(close) => (
              <ConfirmationModal
                close={close}
                isSubmitting={removingIncident}
                headerText={`Remove ${variant}`}
                bodyText={`You are about to remove ${
                  variant === 'accident' ? 'an accident' : 'a violation'
                } from this application. Do you wish to continue?`}
                onSubmit={async () => {
                  const response = await postRemoveIncident({
                    transformResponse: transformAppDetails,
                  });
                  const newIncidents = { ...incidents };
                  delete newIncidents[incidentId];
                  setFieldValue(
                    `drivers.${driverId}.${variant}s`,
                    newIncidents
                  );
                  /* We need to keep track of returned app details in case
                user tries to cancel out of the form, we would then
                set the app details to the most recent returned app details
                to preserve the incidents we have added/removed. */
                  setStatus({ newAppDetails: response.data });
                  setDetails({
                    ...details,
                    commands: response.data.commands,
                    status: response.data.status,
                    effectiveDate: response.data.effectiveDate,
                  });
                  refreshRate();
                }}
              />
            )}
          </Modal>
        )}
      </div>
    </div>
  );
};
